import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react';
import axios from 'axios';
import ManageOEMImageGallery from './ManageOEMImageGallery';
import { attachYearValues, matchesYearFilter } from '../utils/yearFilter';

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export default function ManageOEMModal({
  open,
  onClose,
  initialOEMNumber,
  initialOEMKey,
  onApply,
  canEdit = true
}) {
  const [search, setSearch] = useState(initialOEMNumber || '');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [skuGroups, setSkuGroups] = useState([]); // NOTE: currently unused but left for future use
  const [oemManufacturers, setOemManufacturers] = useState([]);
  const [usage, setUsage] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const readOnly = !canEdit;

  const ensureWritable = useCallback(() => {
    if (!readOnly) return true;
    setError('Viewer access: editing is disabled for your role.');
    return false;
  }, [readOnly]);

  // Filters
  const [fMake, setFMake] = useState('');
  const [fModel, setFModel] = useState('');
  const [fYears, setFYears] = useState('');
  const [fRegion, setFRegion] = useState('');
  const [fItemType, setFItemType] = useState('');
  const [fFitment, setFFitment] = useState('');

  // Searchable SKU Group states
  const [skuGroupSearch, setSkuGroupSearch] = useState('');
  const [skuGroupResults, setSkuGroupResults] = useState([]);
  const [showSkuGroupDropdown, setShowSkuGroupDropdown] = useState(false);
  const [skuGroupBusy, setSkuGroupBusy] = useState(false);
  const [skuGroupNameCache, setSkuGroupNameCache] = useState({}); // Cache for pre-selected group names

  // SKU→Group suggestion states
  const [skuLookupGroups, setSkuLookupGroups] = useState([]); // groups the typed value belongs to as a SKU
  const [skuLookupKey, setSkuLookupKey] = useState(null);     // SKUKey if the typed value is a known SKU
  const [skuLookupBusy, setSkuLookupBusy] = useState(false);
  const [skuLookupDone, setSkuLookupDone] = useState(false);  // true once a lookup has completed for current value
  const [createGroupMode, setCreateGroupMode] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createGroupBusy, setCreateGroupBusy] = useState(false);
  const [createGroupError, setCreateGroupError] = useState('');
  const [mergeWarning, setMergeWarning] = useState(null); // { existingKey, existingName } if proposed name already exists

  const [manufacturerSearch, setManufacturerSearch] = useState('');
  const [showManufacturerDropdown, setShowManufacturerDropdown] = useState(false);

  const [images, setImages] = useState([]);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalFocusKey, setImageModalFocusKey] = useState(null);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [imageBusy, setImageBusy] = useState(false);

  const [form, setForm] = useState({
    OEMKey: null,
    OEMNumber: '',
    SKUGroupKey: null,
    SupersessionOEMKey: null,
    SupersessionOEMNumber: '',
    OEMSampleOrdered: '',
    OEMNote: '',
    OEMProductImage: '',
    OEMManufacturerKey: null,
    NeedsReview: false,
    OEMType: '',
    DateLastUpdated: null
  });

  const applyImagePayload = useCallback(
    (payload) => {
      const arr = Array.isArray(payload?.images)
        ? payload.images
        : Array.isArray(payload)
        ? payload
        : [];
      setImages(arr);
      const primary = arr.find((img) => img?.isPrimary) || arr[0] || null;
      setForm((prev) => {
        const nextValue = primary?.imagePath || '';
        const currentValue = prev.OEMProductImage || '';
        if (currentValue === nextValue) {
          if (!currentValue && !nextValue) return prev;
          return prev;
        }
        if (!nextValue && !currentValue) return prev;
        return { ...prev, OEMProductImage: nextValue };
      });
    },
    [setForm]
  );

  // Debounced search for SKU groups
  const debouncedSkuGroupSearch = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setSkuGroupResults([]);
        return;
      }
      setSkuGroupBusy(true);
      try {
        const { data } = await axios.get('/api/sku-groups/search', {
          params: { query, pageSize: 50 }
        });
        setSkuGroupResults(data || []);
      } catch (err) {
        console.error('SKU group search failed:', err);
        setSkuGroupResults([]);
      } finally {
        setSkuGroupBusy(false);
      }
    }, 300),
    []
  );

  // Trigger search on input change
  useEffect(() => {
    debouncedSkuGroupSearch(skuGroupSearch);
  }, [skuGroupSearch, debouncedSkuGroupSearch]);

  // Debounced SKU→Groups lookup: check if typed value is a known SKU number
  const debouncedSkuLookup = useCallback(
    debounce(async (query) => {
      const q = (query || '').trim();
      if (!q || q.length < 2) {
        setSkuLookupGroups([]);
        setSkuLookupKey(null);
        setSkuLookupDone(false);
        return;
      }
      setSkuLookupBusy(true);
      try {
        const { data } = await axios.get(`/api/sku-by-number/${encodeURIComponent(q)}/groups`);
        setSkuLookupKey(data?.skuKey || null);
        setSkuLookupGroups(data?.groups || []);
        setSkuLookupDone(true);
      } catch {
        setSkuLookupKey(null);
        setSkuLookupGroups([]);
        setSkuLookupDone(false);
      } finally {
        setSkuLookupBusy(false);
      }
    }, 400),
    []
  );

  // Trigger SKU lookup alongside group search
  useEffect(() => {
    debouncedSkuLookup(skuGroupSearch);
  }, [skuGroupSearch, debouncedSkuLookup]);

  // Reset SKU suggestion states when dropdown closes or value cleared
  useEffect(() => {
    if (!showSkuGroupDropdown) {
      setCreateGroupMode(false);
      setNewGroupName('');
      setCreateGroupError('');
      setMergeWarning(null);
    }
  }, [showSkuGroupDropdown]);

  // Create a new SKU Group, optionally add the SKU to it, and select it
  const handleCreateGroupForSku = useCallback(async (groupName) => {
    if (!groupName?.trim()) return;
    setCreateGroupBusy(true);
    setCreateGroupError('');
    setMergeWarning(null);
    try {
      // First check if a group with this name already exists
      const { data: searchHits } = await axios.get('/api/sku-groups/search', {
        params: { query: groupName.trim(), pageSize: 5 }
      });
      const exactMatch = (searchHits || []).find(
        g => (g.SKUGroup || '').trim().toLowerCase() === groupName.trim().toLowerCase()
      );
      if (exactMatch) {
        // Group name already exists — warn user
        setMergeWarning({ existingKey: exactMatch.SKUGroupKey, existingName: exactMatch.SKUGroup });
        setCreateGroupBusy(false);
        return;
      }

      // Create group and link SKU using the resolve endpoint
      const payload = {
        sku: skuLookupKey ? skuGroupSearch.trim() : null,
        skuGroupName: groupName.trim(),
        createGroup: true,
        createSku: false
      };
      const { data: result } = await axios.post('/api/kits/import-resolve-sku', payload);
      if (result?.skuGroupKey) {
        onField('SKUGroupKey', result.skuGroupKey);
        setSkuGroupSearch(groupName.trim());
        setSkuGroupNameCache(prev => ({ ...prev, [result.skuGroupKey]: groupName.trim() }));
        setShowSkuGroupDropdown(false);
        setCreateGroupMode(false);
        setNewGroupName('');
      }
    } catch (err) {
      setCreateGroupError(err?.response?.data?.error || 'Failed to create group');
    } finally {
      setCreateGroupBusy(false);
    }
  }, [skuGroupSearch, skuLookupKey]);

  // Use existing group for SKU (add SKU to group if needed, then select it)
  const handleUseExistingGroupForSku = useCallback(async (group) => {
    if (!group?.SKUGroupKey) return;
    try {
      // Link the SKU to this group if we know the SKU exists
      if (skuLookupKey) {
        await axios.post('/api/kits/import-resolve-sku', {
          sku: skuGroupSearch.trim(),
          addToGroupKey: group.SKUGroupKey,
          createSku: false
        });
      }
      onField('SKUGroupKey', group.SKUGroupKey);
      setSkuGroupSearch(group.SKUGroup || '');
      setSkuGroupNameCache(prev => ({ ...prev, [group.SKUGroupKey]: group.SKUGroup }));
      setShowSkuGroupDropdown(false);
    } catch (err) {
      console.error('Failed to link SKU to group:', err);
      // Still select the group even if linking fails
      onField('SKUGroupKey', group.SKUGroupKey);
      setSkuGroupSearch(group.SKUGroup || '');
      setShowSkuGroupDropdown(false);
    }
  }, [skuGroupSearch, skuLookupKey]);

  // Merge: user confirmed they want to use the existing group with the same name
  const handleMergeConfirm = useCallback(async () => {
    if (!mergeWarning?.existingKey) return;
    setCreateGroupBusy(true);
    try {
      // Link SKU to the existing group if possible
      if (skuLookupKey) {
        await axios.post('/api/kits/import-resolve-sku', {
          sku: skuGroupSearch.trim(),
          addToGroupKey: mergeWarning.existingKey,
          createSku: false
        });
      }
      onField('SKUGroupKey', mergeWarning.existingKey);
      setSkuGroupSearch(mergeWarning.existingName);
      setSkuGroupNameCache(prev => ({ ...prev, [mergeWarning.existingKey]: mergeWarning.existingName }));
      setShowSkuGroupDropdown(false);
      setCreateGroupMode(false);
      setMergeWarning(null);
      setNewGroupName('');
    } catch (err) {
      setCreateGroupError(err?.response?.data?.error || 'Failed to merge');
    } finally {
      setCreateGroupBusy(false);
    }
  }, [mergeWarning, skuGroupSearch, skuLookupKey]);

  // Helper to load SKU Group name (used for preload and onFocus)
  const loadSkuGroupName = useCallback(
    async (key) => {
      if (!key || skuGroupNameCache[key]) {
        if (skuGroupNameCache[key]) setSkuGroupSearch(skuGroupNameCache[key]);
        return;
      }
      try {
        const res = await axios.get(`/api/sku-groups/${key}`);
        const name = res.data?.SKUGroup || '';
        setSkuGroupSearch(name);
        setSkuGroupNameCache((prev) => ({ ...prev, [key]: name }));
      } catch {
        // Ignore fetch errors; leave empty
      }
    },
    [skuGroupNameCache]
  );

  // Infinite scroll for usage
  const [visibleCount, setVisibleCount] = useState(100);
  const gridRef = useRef(null);

  // Results infinite scroll
  const resultsRef = useRef(null);
  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [hasMore, setHasMore] = useState(false);

  // Reset when closing
  useEffect(() => {
    if (!open) {
      setSearch(initialOEMNumber || '');
      setResults([]);
      setSelected(null);
      setUsage([]);
      setError('');
      setBusy(false);
      setForm({
        OEMKey: null,
        OEMNumber: '',
        SKUGroupKey: null,
        SupersessionOEMKey: null,
        SupersessionOEMNumber: '',
        OEMSampleOrdered: '',
        OEMNote: '',
        OEMProductImage: '',
        OEMManufacturerKey: null,
        NeedsReview: false,
        OEMType: '',
        DateLastUpdated: null
      });
      setFMake('');
      setFModel('');
      setFYears('');
      setFRegion('');
      setFItemType('');
      setFFitment('');
      setVisibleCount(100);

      // Reset SKU search states
      setSkuGroupSearch('');
      setSkuGroupResults([]);
      setShowSkuGroupDropdown(false);
      setSkuGroupBusy(false);
      setSkuGroupNameCache({});
      setSkuLookupGroups([]);
      setSkuLookupKey(null);
      setSkuLookupBusy(false);
      setSkuLookupDone(false);
      setCreateGroupMode(false);
      setNewGroupName('');
      setCreateGroupBusy(false);
      setCreateGroupError('');
      setMergeWarning(null);
      setManufacturerSearch('');
      setShowManufacturerDropdown(false);

      setImages([]);
      setImageModalOpen(false);
      setImageModalFocusKey(null);
      setImageUrlInput('');
      setImageBusy(false);
    }
  }, [open, initialOEMNumber, initialOEMKey]);

  // Load lookups + preselect by number (if provided)
  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const mfRes = await axios.get('/api/oem-manufacturers');
        setOemManufacturers(mfRes.data || []);
      } catch {
        /* ignore */
      }

      if (initialOEMKey) {
        // exact: use the OEMKey from the selected row
        console.log('Loading OEM by key:', initialOEMKey);
        await loadOne(initialOEMKey);
      } else if (initialOEMNumber && initialOEMNumber.trim()) {
        // Always populate the OEMNumber first to avoid blank field
        setForm((prev) => ({ ...prev, OEMNumber: initialOEMNumber }));
        // fallback: search by number async, load if hit (overwrites if found)
        console.log('Searching OEM by number:', initialOEMNumber);
        try {
          const { data } = await axios.get('/api/oems/search', {
            params: { query: initialOEMNumber }
          });
          const hit =
            (data || []).find(
              (r) => (r.OEMNumber || '').trim() === initialOEMNumber.trim()
            ) || (data || [])[0];
          if (hit) {
            console.log('Found OEM hit:', hit.OEMNumber);
            await loadOne(hit.OEMKey);
          } else {
            console.warn('No OEM hit for:', initialOEMNumber);
            // No hit: Stay in "new" mode with populated number
            setSelected(null);
            setUsage([]);
          }
        } catch (err) {
          console.error('OEM search failed:', err);
          // Error: Stay in "new" mode
        }
      }
    })();
  }, [open, initialOEMNumber, initialOEMKey]);

  const onField = useCallback(
    (name, value, { force = false } = {}) => {
      if (readOnly && !force) return;
      setForm((prev) => ({ ...prev, [name]: value }));
    },
    [readOnly]
  );

  const lastUpdatedDisplay = useMemo(() => {
    if (!form.DateLastUpdated) return '—';
    const when = new Date(form.DateLastUpdated);
    if (Number.isNaN(when.getTime())) return '—';
    return when.toLocaleString();
  }, [form.DateLastUpdated]);

  const primaryImage = useMemo(() => {
    if (!images.length) return null;
    return images.find((img) => img.isPrimary) || images[0];
  }, [images]);

  const manufacturerMatches = useMemo(() => {
    const query = manufacturerSearch.trim().toLowerCase();
    if (!query) return oemManufacturers;
    return oemManufacturers.filter((m) =>
      (m.OEMManufacturer || '').toLowerCase().includes(query)
    );
  }, [manufacturerSearch, oemManufacturers]);

  useEffect(() => {
    if (!open) return;
    if (form.OEMManufacturerKey == null) return;
    const current = oemManufacturers.find(
      (m) => m.OEMManufacturerKey === form.OEMManufacturerKey
    );
    if (current && current.OEMManufacturer !== manufacturerSearch) {
      setManufacturerSearch(current.OEMManufacturer || '');
    }
  }, [form.OEMManufacturerKey, oemManufacturers, manufacturerSearch, open]);

  const primaryImageAbsoluteUrl = useMemo(() => {
    if (!primaryImage?.imageUrl) return '';
    try {
      if (typeof window !== 'undefined') {
        return new URL(primaryImage.imageUrl, window.location.origin).toString();
      }
    } catch (err) {
      console.warn('Failed to build absolute OEM image URL', err);
      return primaryImage.imageUrl;
    }
    return primaryImage.imageUrl;
  }, [primaryImage]);

  const imageSlots = useMemo(
    () => Array.from({ length: 8 }, (_, idx) => images[idx] || null),
    [images]
  );
  const canCaptureMore = useMemo(() => images.length < 8, [images]);

  const handleUseInKit = useCallback(() => {
    if (!ensureWritable()) return;
    if (typeof onApply !== 'function') return;
    const skuGroupName = form.SKUGroupKey
      ? (skuGroupSearch?.trim() ||
          skuGroupNameCache[form.SKUGroupKey] ||
          '')
      : '';
    onApply({
      OEMKey: form.OEMKey ?? null,
      OEMNumber: form.OEMNumber || search || initialOEMNumber || '',
      SKUGroupKey: form.SKUGroupKey ?? null,
      SKUGroup: skuGroupName
    });
    if (typeof onClose === 'function') onClose();
  }, [
    ensureWritable,
    onApply,
    form,
    skuGroupSearch,
    skuGroupNameCache,
    onClose,
    search,
    initialOEMNumber
  ]);

  async function doSearch(reset = true) {
    setError('');
    const q = search.trim();
    const nextPage = reset ? 1 : page + 1;
    const { data } = await axios.get('/api/oems/search', {
      params: { query: q, page: nextPage, pageSize }
    });
    setResults((prev) =>
      reset ? data || [] : [...(prev || []), ...(data || [])]
    );
    setPage(nextPage);
    setHasMore(Array.isArray(data) && data.length === pageSize);
  }

  async function loadMoreIfNeeded(e) {
    const el = e?.currentTarget || resultsRef.current;
    if (!el || !hasMore || busy) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (nearBottom) {
      await doSearch(false);
    }
  }

  const fetchOEMImages = useCallback(
    async (oemKey) => {
      if (!Number.isInteger(oemKey)) {
        applyImagePayload([]);
        return;
      }
      try {
        const { data } = await axios.get(`/api/oems/${oemKey}/images`);
        applyImagePayload(data);
      } catch (err) {
        console.error('Failed to load OEM images', err);
        applyImagePayload([]);
      }
    },
    [applyImagePayload]
  );

  const handleOpenImageModal = useCallback(
    (imageKey = null) => {
      if (!form.OEMKey) {
        setError('Select or save an OEM before managing images.');
        return;
      }
      setError('');
      setImageModalFocusKey(imageKey ?? null);
      setImageModalOpen(true);
    },
    [form.OEMKey]
  );

  const handleCloseImageModal = useCallback(() => {
    setImageModalOpen(false);
    setImageModalFocusKey(null);
  }, []);

  const handleCaptureImage = useCallback(async () => {
    if (!ensureWritable()) return;
    if (!form.OEMKey) {
      setError('Save the OEM before capturing images.');
      return;
    }
    if (!canCaptureMore) {
      setError('Maximum of eight images already stored for this OEM.');
      return;
    }
    const trimmed = imageUrlInput.trim();
    if (!trimmed) {
      setError('Provide a source URL to capture.');
      return;
    }
    try {
      setImageBusy(true);
      setError('');
      const res = await axios.post(`/api/oems/${form.OEMKey}/images`, {
        sourceUrl: trimmed
      });
      applyImagePayload(res.data);
      setImageUrlInput('');
    } catch (err) {
      console.error('Capture image failed', err);
      setError(err?.response?.data?.error || 'Failed to capture image.');
    } finally {
      setImageBusy(false);
    }
  }, [
    ensureWritable,
    form.OEMKey,
    imageUrlInput,
    canCaptureMore,
    applyImagePayload
  ]);

  const handleMakePrimaryImage = useCallback(
    async (imageKey) => {
      if (!ensureWritable()) return false;
      if (!form.OEMKey) return false;
      try {
        setImageBusy(true);
        setError('');
        const res = await axios.patch(
          `/api/oems/${form.OEMKey}/images/${imageKey}/default`
        );
        applyImagePayload(res.data);
        return true;
      } catch (err) {
        console.error('Failed to set OEM image as default', err);
        setError(err?.response?.data?.error || 'Failed to set default image.');
        return false;
      } finally {
        setImageBusy(false);
      }
    },
    [ensureWritable, form.OEMKey, applyImagePayload]
  );

  const handleDeleteImage = useCallback(
    async (imageKey) => {
      if (!ensureWritable()) return false;
      if (!form.OEMKey) return false;
      try {
        setImageBusy(true);
        setError('');
        const res = await axios.delete(
          `/api/oems/${form.OEMKey}/images/${imageKey}`
        );
        applyImagePayload(res.data);
        return true;
      } catch (err) {
        console.error('Failed to delete OEM image', err);
        setError(err?.response?.data?.error || 'Failed to delete image.');
        return false;
      } finally {
        setImageBusy(false);
      }
    },
    [ensureWritable, form.OEMKey, applyImagePayload]
  );

  const handleReorderImages = useCallback(
    async (order) => {
      if (!ensureWritable()) return false;
      if (!form.OEMKey || !Array.isArray(order) || !order.length) return false;
      try {
        setImageBusy(true);
        setError('');
        const res = await axios.patch(
          `/api/oems/${form.OEMKey}/images/reorder`,
          { order }
        );
        applyImagePayload(res.data);
        return true;
      } catch (err) {
        console.error('Failed to reorder OEM images', err);
        setError(err?.response?.data?.error || 'Failed to reorder images.');
        return false;
      } finally {
        setImageBusy(false);
      }
    },
    [ensureWritable, form.OEMKey, applyImagePayload]
  );

  const handleRefreshImages = useCallback(async () => {
    if (!form.OEMKey) return;
    await fetchOEMImages(form.OEMKey);
  }, [form.OEMKey, fetchOEMImages]);

  const handleCopyPrimaryUrl = useCallback(() => {
    if (!primaryImageAbsoluteUrl) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(primaryImageAbsoluteUrl).catch(() => {});
    } else if (typeof window !== 'undefined') {
      window.prompt('Primary image URL', primaryImageAbsoluteUrl);
    }
  }, [primaryImageAbsoluteUrl]);

  const handleOpenPrimaryInNewTab = useCallback(() => {
    if (!primaryImage?.imageUrl) return;
    const target = primaryImageAbsoluteUrl || primaryImage.imageUrl;
    if (typeof window !== 'undefined') {
      window.open(target, '_blank', 'noopener,noreferrer');
    }
  }, [primaryImage, primaryImageAbsoluteUrl]);

  async function loadOne(oemKey) {
    setError('');
    const { data } = await axios.get(`/api/oems/${oemKey}`);
    if (!data) {
      setSelected(null);
      return;
    }
    setSelected(data);
    setForm({
      OEMKey: data.OEMKey,
      OEMNumber: data.OEMNumber || '',
      SKUGroupKey: data.SKUGroupKey ?? null,
      SupersessionOEMKey: data.SupersessionOEMKey ?? null,
      SupersessionOEMNumber: data.SupersessionOEMNumber || '',
      OEMSampleOrdered: data.OEMSampleOrdered || '',
      OEMNote: data.OEMNote || '',
      OEMProductImage: data.OEMProductImage || '',
      OEMManufacturerKey: data.OEMManufacturerKey ?? null,
      NeedsReview: !!data.NeedsReview,
      OEMType: data.OEMType || '',
      DateLastUpdated: data.DateLastUpdated || null
    });
    setShowManufacturerDropdown(false);
    setImageUrlInput('');
    await fetchOEMImages(data.OEMKey);
    setImageModalFocusKey(null);
    setImageModalOpen(false);
    // Clear and preload SKU Group name immediately if key exists (refreshes on change)
    setSkuGroupSearch('');
    if (data.SKUGroupKey) {
      await loadSkuGroupName(data.SKUGroupKey);
    }
    const usageRes = await axios.get(`/api/oems/${oemKey}/usage`);
    setUsage(Array.isArray(usageRes.data) ? usageRes.data : []);
  }

  async function save() {
    if (!ensureWritable()) return;
    try {
      setBusy(true);
      setError('');
      const body = { ...form };
      delete body.DateLastUpdated;

      // Resolve Supersession OEMNumber → Key if needed
      if (body.SupersessionOEMNumber && !body.SupersessionOEMKey) {
        try {
          const sr = await axios.get('/api/oems/search', {
            params: { query: body.SupersessionOEMNumber }
          });
          const hit = (sr.data || []).find(
            (x) =>
              (x.OEMNumber || '').trim() ===
              body.SupersessionOEMNumber.trim()
          );
          if (hit) body.SupersessionOEMKey = hit.OEMKey;
        } catch {
          /* ignore */
        }
      }

      ['OEMSampleOrdered', 'OEMNote', 'OEMProductImage'].forEach((key) => {
        if (typeof body[key] === 'string' && body[key].trim() === '') {
          body[key] = null;
        }
      });

      if (body.OEMKey) {
        await axios.patch(`/api/oems/${body.OEMKey}`, body);
        await loadOne(body.OEMKey);
      } else {
        const { data } = await axios.post('/api/oems', body);
        if (data?.OEMKey) await loadOne(data.OEMKey);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function duplicateCurrentOEM() {
    if (!ensureWritable()) return;
    if (!form.OEMKey) {
      setError('Select or save an OEM before duplicating.');
      return;
    }

    const fromNumber = String(form.OEMNumber || '').trim();
    const promptText = `Duplicate OEM\nCopying from: ${fromNumber || `#${form.OEMKey}`}\n\nNew OEM number:`;
    const nextRaw = window.prompt(promptText, fromNumber ? `${fromNumber}` : '');
    if (nextRaw == null) return;
    const next = String(nextRaw).trim();
    if (!next) {
      setError('New OEM number is required.');
      return;
    }

    try {
      setBusy(true);
      setError('');
      const { data } = await axios.post(`/api/oems/${form.OEMKey}/duplicate`, {
        newOEMNumber: next
      });
      if (data?.OEMKey) {
        await loadOne(data.OEMKey);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Duplicate failed.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!ensureWritable()) return;
    if (!form.OEMKey) return;
    if (
      !window.confirm(
        `Delete OEM ${form.OEMNumber}? This cannot be undone.`
      )
    )
      return;
    try {
      setBusy(true);
      setError('');
      await axios.delete(`/api/oems/${form.OEMKey}`);
      setSelected(null);
      setUsage([]);
      setImages([]);
      setImageUrlInput('');
      setImageModalOpen(false);
      setImageModalFocusKey(null);
      setImageBusy(false);
      setForm({
        OEMKey: null,
        OEMNumber: '',
        SKUGroupKey: null,
        SupersessionOEMKey: null,
        SupersessionOEMNumber: '',
        OEMSampleOrdered: '',
        OEMNote: '',
        OEMProductImage: '',
        OEMManufacturerKey: null,
        NeedsReview: false,
        OEMType: '',
        DateLastUpdated: null
      });
      if (search.trim()) await doSearch();
    } catch (err) {
      setError(err?.response?.data?.error || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  // Build grouped usage rows and sort
  const usageRows = useMemo(() => {
    // Group by Make|ModelLabel|Region|ItemType|FitmentLocation
    const groups = new Map();
    for (const r of usage) {
      const modelLabel = `${r.ModelName}${
        r.StreetName ? ' ' + r.StreetName : ''
      }`;
      const rawSort = Number(
        r.SortOrder ?? r.ModelSortOrder ?? r.Model_SortOrder
      );
      const sortOrder = Number.isFinite(rawSort) ? rawSort : 999999;
      const key = [
        r.Make,
        modelLabel,
        r.Region,
        r.ItemType,
        r.FitmentLocation
      ].join('§');
      const additional = r.AdditionalFitmentInfo || '';
      if (!groups.has(key)) {
        groups.set(key, {
          years: [],
          modelSort: sortOrder,
          additional
        });
      }
      const g = groups.get(key);
      if (!g.additional && additional) {
        g.additional = additional;
      }
      g.years.push(Number(r.Year));
    }

    const toRanges = (ordered) => {
      if (!ordered.length) return '';
      const ranges = [];
      let start = ordered[0];
      let prev = ordered[0];
      for (let i = 1; i < ordered.length; i++) {
        if (ordered[i] === prev + 1) {
          prev = ordered[i];
        } else {
          ranges.push(
            start === prev ? `${start}` : `${start}-${prev}`
          );
          start = ordered[i];
          prev = ordered[i];
        }
      }
      if (start !== undefined) {
        ranges.push(
          start === prev ? `${start}` : `${start}-${prev}`
        );
      }
      return ranges.join(', ');
    };

    return [...groups.entries()]
      .map(([key, g]) => {
        const [Make, Model, Region, ItemType, FitmentLocation] =
          key.split('§');
        const orderedYears = attachYearValues(g.years);
        return {
          Make,
          Model,
          Years: orderedYears.length ? toRanges(orderedYears) : '',
          yearValues: orderedYears,
          Region,
          ItemType,
          FitmentLocation,
          modelSort: g.modelSort,
          AdditionalFitmentInfo: g.additional || ''
        };
      })
      .sort(
        (a, b) =>
          a.modelSort - b.modelSort ||
          (a.ItemType || '').localeCompare(b.ItemType || '') ||
          (a.FitmentLocation || '').localeCompare(
            b.FitmentLocation || ''
          ) ||
          (a.AdditionalFitmentInfo || '').localeCompare(
            b.AdditionalFitmentInfo || ''
          ) ||
          a.Model.localeCompare(b.Model)
      );
  }, [usage]);

  // Filtered and visible
  const filteredRows = useMemo(() => {
    let res = [...usageRows];
    const lc = (str) => (str || '').toLowerCase();
    if (fMake.trim())
      res = res.filter((r) =>
        lc(r.Make).includes(lc(fMake))
      );
    if (fModel.trim())
      res = res.filter((r) =>
        lc(r.Model).includes(lc(fModel))
      );
    if (fYears.trim())
      res = res.filter((r) =>
        matchesYearFilter(r.yearValues, r.Years, fYears)
      );
    if (fRegion.trim())
      res = res.filter((r) =>
        lc(r.Region).includes(lc(fRegion))
      );
    if (fItemType.trim())
      res = res.filter((r) =>
        lc(r.ItemType).includes(lc(fItemType))
      );
    if (fFitment.trim())
      res = res.filter((r) =>
        lc(r.FitmentLocation).includes(lc(fFitment))
      );
    return res;
  }, [
    usageRows,
    fMake,
    fModel,
    fYears,
    fRegion,
    fItemType,
    fFitment
  ]);

  const visibleRows = filteredRows.slice(0, visibleCount);
  const filteredCount = filteredRows.length;
  const totalRows = usageRows.length;

  async function onGridScroll(e) {
    const el = e?.currentTarget || gridRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (nearBottom && visibleCount < filteredRows.length) {
      setVisibleCount((prev) =>
        Math.min(prev + 100, filteredRows.length)
      );
    }
  }

  function clearFilters() {
    setFMake('');
    setFModel('');
    setFYears('');
    setFRegion('');
    setFItemType('');
    setFFitment('');
  }

  function copyUsage() {
    const head =
      'Make\tModel\tYears\tRegion\tItemType\tFitmentLocation';
    const lines = filteredRows.map((r) =>
      [r.Make, r.Model, r.Years, r.Region, r.ItemType, r.FitmentLocation].join(
        '\t'
      )
    );
    navigator.clipboard.writeText(
      [head, ...lines].join('\n')
    );
  }

  if (!open) return null;

  return (
    <>
      <div
        className="pim-modal-surface"
        style={{
          position: 'fixed',
          left: '50%',
          top: '5vh',
          transform: 'translateX(-50%)',
          width: '90vw',
          maxWidth: '1800px',
          maxHeight: '90vh',
          zIndex: 5001,
          display: 'grid',
          gridTemplateRows: 'auto minmax(0,1fr)'
        }}
      >
        <div className="pim-modal-surface__header">
          <div
            style={{ fontWeight: 600, fontSize: '16px' }}
          >
            Manage OEM{' '}
            <span
              style={{
                color: 'var(--modal-text-gray)',
                fontWeight: 400,
                marginLeft: '8px'
              }}
            >
              Usage ({filteredCount} / {totalRows})
            </span>
          </div>
          <button
            style={{
              background: 'var(--modal-bg-light-blue)',
              color: 'var(--modal-text-dark)',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '0px'
            }}
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="pim-modal-surface__body pim-modal-stack">
          {error && (
            <div
              className="pim-modal-alert pim-modal-alert--error"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <span style={{ flex: 1 }}>{error}</span>
              <button
                onClick={() => setError('')}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'var(--modal-text-white)',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '0px'
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          {readOnly && (
            <div className="pim-modal-alert pim-modal-alert--info">
              Viewer access: this modal is read-only.
            </div>
          )}

          <div className="pim-modal-layout">
            {/* Left-hand search column */}
            <div className="pim-modal-stack">
              <div
                className="pim-modal-card"
                style={{ display: 'grid', gap: '8px' }}
              >
                <div
                  style={{ display: 'flex', gap: '8px' }}
                >
                  <input
                    placeholder="Search OEMNumber…"
                    value={search}
                    onChange={(e) =>
                      setSearch(e.target.value)
                    }
                    onKeyDown={(e) =>
                      e.key === 'Enter' && doSearch()
                    }
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border:
                        '1px solid var(--modal-input-border-gray)',
                      borderRadius: '6px'
                    }}
                  />
                  <button
                    style={{
                      background: 'var(--modal-bg-light-blue)',
                      color: 'var(--modal-text-dark)',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '0px'
                    }}
                    onClick={doSearch}
                  >
                    Search
                  </button>
                  <button
                    style={{
                      background: 'var(--modal-bg-blue)',
                      color: 'var(--modal-text-white)',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '0px'
                    }}
                    onClick={() => {
                      if (readOnly) return;
                      setForm({
                        OEMKey: null,
                        OEMNumber: search,
                        SKUGroupKey: null,
                        SupersessionOEMKey: null,
                        SupersessionOEMNumber: '',
                        OEMSampleOrdered: '',
                        OEMNote: '',
                        OEMProductImage: '',
                        OEMManufacturerKey: null,
                        NeedsReview: false,
                        OEMType: '',
                        DateLastUpdated: null
                      });
                      setManufacturerSearch('');
                      setShowManufacturerDropdown(false);
                      setImages([]);
                      setImageModalOpen(false);
                      setImageModalFocusKey(null);
                      setImageUrlInput('');
                      setImageBusy(false);
                    }}
                    disabled={readOnly}
                  >
                    New OEM
                  </button>
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: 'var(--modal-text-gray)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>
                    {results.length
                      ? `${results.length} result${
                          results.length === 1 ? '' : 's'
                        }`
                      : 'No results yet'}
                  </span>
                  {selected?.OEMKey ? (
                    <span
                      style={{
                        color: 'var(--modal-text-dark)'
                      }}
                    >
                      Selected #{selected.OEMKey}
                    </span>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              </div>

              <div
                className="pim-modal-card pim-modal-scroll"
                style={{ flex: 1, padding: '4px' }}
                onScroll={loadMoreIfNeeded}
                ref={resultsRef}
              >
                {(results || []).map((r) => (
                  <div
                    key={r.OEMKey}
                    onClick={() => loadOne(r.OEMKey)}
                    style={{
                      cursor: 'pointer',
                      padding: '0.5rem',
                      background:
                        selected?.OEMKey === r.OEMKey
                          ? 'var(--highlight)'
                          : 'transparent',
                      borderBottom:
                        '1px solid var(--border)'
                    }}
                  >
                    {r.OEMNumber} (OEMKey #{r.OEMKey})
                  </div>
                ))}
                {hasMore && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '6px'
                    }}
                  >
                    <button
                      onClick={() => doSearch(false)}
                    >
                      Load more
                    </button>
                  </div>
                )}
                {!results.length && (
                  <div
                    style={{
                      color: 'var(--modal-text-gray)',
                      fontSize: '12px',
                      padding: '6px'
                    }}
                  >
                    No results
                  </div>
                )}
              </div>
            </div>

            {/* Right-hand rail */}
            <div
              className="pim-modal-rail"
              style={{ minHeight: 0 }}
            >
              {/* OEM fields */}
              <div
                className="pim-modal-card"
                style={{
                  display: 'grid',
                  gap: '12px',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(220px, 1fr))',
                  alignItems: 'start'
                }}
              >
                <label
                  style={{
                    display: 'grid',
                    gap: '6px',
                    minWidth: 0
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    OEM Number
                  </span>
                  <input
                    value={form.OEMNumber}
                    onChange={(e) =>
                      onField('OEMNumber', e.target.value)
                    }
                    readOnly={readOnly}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border:
                        '1px solid var(--modal-input-border-gray)',
                      borderRadius: '6px'
                    }}
                  />
                </label>

                <label
                  style={{
                    display: 'grid',
                    gap: '6px',
                    minWidth: 0
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    Supersession (OEM Number)
                  </span>
                  <input
                    value={form.SupersessionOEMNumber || ''}
                    onChange={(e) =>
                      onField(
                        'SupersessionOEMNumber',
                        e.target.value
                      )
                    }
                    readOnly={readOnly}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border:
                        '1px solid var(--modal-input-border-gray)',
                      borderRadius: '6px'
                    }}
                  />
                </label>

                {/* SKU Group */}
                <div
                  style={{
                    display: 'grid',
                    gap: '6px',
                    position: 'relative',
                    minWidth: 0
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    SKU Group
                  </span>
                  <input
                    type="text"
                    placeholder="— Type to search SKU Group —"
                    value={skuGroupSearch}
                    readOnly={readOnly}
                    onChange={(e) => {
                      if (readOnly) return;
                      setSkuGroupSearch(e.target.value);
                      if (form.SKUGroupKey)
                        onField('SKUGroupKey', null);
                    }}
                    onDoubleClick={() => {
                      if (readOnly) return;
                      onField('SKUGroupKey', null);
                      setSkuGroupSearch('');
                      setShowSkuGroupDropdown(true);
                    }}
                    onFocus={() => {
                      if (readOnly) return;
                      setShowSkuGroupDropdown(true);
                      if (
                        form.SKUGroupKey &&
                        !skuGroupSearch.trim()
                      ) {
                        loadSkuGroupName(form.SKUGroupKey);
                      }
                    }}
                    onBlur={() =>
                      !readOnly &&
                      setTimeout(
                        () =>
                          setShowSkuGroupDropdown(false),
                        300
                      )
                    }
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border:
                        '1px solid var(--modal-input-border-gray)',
                      borderRadius: '6px'
                    }}
                  />
                  {showSkuGroupDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '280px',
                        overflowY: 'auto',
                        background:
                          'var(--modal-bg-white-cell)',
                        border:
                          '1px solid var(--modal-input-border-gray)',
                        borderTop: 'none',
                        padding: 0,
                        margin: 0,
                        zIndex: 10,
                        boxShadow:
                          '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {/* ── SKU→Group suggestions section ── */}
                      {skuGroupSearch.trim().length >= 2 && skuLookupDone && skuLookupKey && (
                        <div style={{ borderBottom: '2px solid var(--modal-border-gray)' }}>
                          <div style={{ padding: '6px 8px', fontSize: '11px', fontWeight: 600, color: 'var(--modal-text-gray)', background: 'var(--modal-bg-gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            SKU "{skuGroupSearch.trim()}" belongs to:
                          </div>
                          {skuLookupGroups.length > 0 ? (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {skuLookupGroups.map((g) => (
                                <li
                                  key={`sku-grp-${g.SKUGroupKey}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUseExistingGroupForSku(g);
                                  }}
                                  style={{
                                    padding: '8px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid var(--modal-border-gray)',
                                    background: form.SKUGroupKey === g.SKUGroupKey ? 'var(--modal-bg-gray)' : 'transparent',
                                    display: 'flex', alignItems: 'center', gap: '6px'
                                  }}
                                >
                                  <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 600, background: '#22c55e', color: '#fff' }}>USE</span>
                                  {g.SKUGroup} {g.SKUGroupDescription ? `(${g.SKUGroupDescription})` : ''}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div style={{ padding: '8px', fontSize: '12px', color: 'var(--modal-text-gray)' }}>
                              This SKU is not in any group yet.
                            </div>
                          )}

                          {/* Create new group for this SKU */}
                          {!createGroupMode ? (
                            <div style={{ padding: '6px 8px' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setCreateGroupMode(true);
                                  setNewGroupName(skuGroupSearch.trim().replace(/[A-Za-z]+$/, '') || skuGroupSearch.trim());
                                  setCreateGroupError('');
                                  setMergeWarning(null);
                                }}
                                style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '4px', border: '1px solid var(--modal-input-border-gray)', background: 'var(--modal-bg-gray)', cursor: 'pointer', color: 'inherit' }}
                              >
                                + Create New Group for this SKU
                              </button>
                            </div>
                          ) : (
                            <div style={{ padding: '8px', borderTop: '1px solid var(--modal-border-gray)' }}>
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div>
                                  <label style={{ fontSize: '11px', color: 'var(--modal-text-gray)', display: 'block', marginBottom: '2px' }}>New Group Name</label>
                                  <input
                                    type="text"
                                    value={newGroupName}
                                    onChange={(e) => { setNewGroupName(e.target.value); setMergeWarning(null); }}
                                    style={{ padding: '4px 6px', border: '1px solid var(--modal-input-border-gray)', borderRadius: '4px', width: '140px', fontSize: '12px' }}
                                    placeholder="e.g. 011111"
                                  />
                                </div>
                                <button
                                  type="button"
                                  disabled={createGroupBusy || !newGroupName.trim()}
                                  onClick={() => handleCreateGroupForSku(newGroupName)}
                                  style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '4px', border: 'none', background: '#2563eb', color: '#fff', cursor: createGroupBusy || !newGroupName.trim() ? 'not-allowed' : 'pointer', opacity: createGroupBusy || !newGroupName.trim() ? 0.5 : 1 }}
                                >
                                  {createGroupBusy ? 'Creating…' : 'Create & Use'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setCreateGroupMode(false); setMergeWarning(null); setCreateGroupError(''); }}
                                  style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--modal-input-border-gray)', background: 'transparent', cursor: 'pointer', color: 'inherit' }}
                                >
                                  Cancel
                                </button>
                              </div>
                              {mergeWarning && (
                                <div style={{ marginTop: '6px', padding: '6px 8px', background: '#fef3c7', borderRadius: '4px', fontSize: '12px', color: '#92400e' }}>
                                  A group named "<strong>{mergeWarning.existingName}</strong>" already exists (Key: {mergeWarning.existingKey}).
                                  <div style={{ marginTop: '4px', display: 'flex', gap: '6px' }}>
                                    <button
                                      type="button"
                                      onClick={handleMergeConfirm}
                                      disabled={createGroupBusy}
                                      style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '3px', border: 'none', background: '#f59e0b', color: '#fff', cursor: 'pointer' }}
                                    >
                                      Use Existing Group
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setMergeWarning(null)}
                                      style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '3px', border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: 'inherit' }}
                                    >
                                      Change Name
                                    </button>
                                  </div>
                                </div>
                              )}
                              {createGroupError && (
                                <div style={{ marginTop: '4px', fontSize: '11px', color: '#dc2626' }}>{createGroupError}</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Loading indicator for SKU lookup ── */}
                      {skuGroupSearch.trim().length >= 2 && skuLookupBusy && (
                        <div style={{ padding: '6px 8px', fontSize: '11px', color: 'var(--modal-text-gray)', fontStyle: 'italic', borderBottom: '1px solid var(--modal-border-gray)' }}>
                          Looking up SKU…
                        </div>
                      )}

                      {/* ── Normal SKU Group search results ── */}
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {skuGroupBusy ? (
                          <li
                            style={{
                              padding: '8px',
                              color: 'var(--modal-text-gray)',
                              fontStyle: 'italic'
                            }}
                          >
                            Searching groups...
                          </li>
                        ) : skuGroupResults.length === 0 && !skuLookupDone ? (
                          <li
                            style={{
                              padding: '8px',
                              color: 'var(--modal-text-gray)',
                              fontStyle: 'italic'
                            }}
                          >
                            {skuGroupSearch.trim() ? 'No matching groups' : 'Type to search'}
                          </li>
                        ) : (
                          skuGroupResults.map((g) => (
                            <li
                              key={g.SKUGroupKey}
                              onClick={(e) => {
                                e.stopPropagation();
                                onField(
                                  'SKUGroupKey',
                                  g.SKUGroupKey
                                );
                                setSkuGroupSearch(g.SKUGroup);
                                setShowSkuGroupDropdown(false);
                                setSkuGroupResults([]);
                              }}
                              style={{
                                padding: '8px',
                                cursor: 'pointer',
                                borderBottom:
                                  '1px solid var(--modal-border-gray)',
                                background:
                                  form.SKUGroupKey ===
                                  g.SKUGroupKey
                                    ? 'var(--modal-bg-gray)'
                                    : 'transparent',
                                color:
                                  form.SKUGroupKey ===
                                  g.SKUGroupKey
                                    ? 'var(--modal-text)'
                                    : 'inherit'
                              }}
                            >
                              {g.SKUGroup}{' '}
                              {g.SKUGroupDescription
                                ? `(${g.SKUGroupDescription})`
                                : ''}
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                <label
                  style={{
                    display: 'grid',
                    gap: '6px',
                    minWidth: 0
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    OEM Sample Ordered
                  </span>
                  <input
                    value={form.OEMSampleOrdered}
                    onChange={(e) =>
                      onField(
                        'OEMSampleOrdered',
                        e.target.value
                      )
                    }
                    readOnly={readOnly}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border:
                        '1px solid var(--modal-input-border-gray)',
                      borderRadius: '6px'
                    }}
                  />
                </label>

                {/* Manufacturer */}
                <div
                  style={{
                    display: 'grid',
                    gap: '6px',
                    minWidth: 0,
                    position: 'relative'
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    OEM Manufacturer
                  </span>
                  <input
                    value={manufacturerSearch}
                    onChange={(e) => {
                      if (readOnly) return;
                      setManufacturerSearch(
                        e.target.value
                      );
                      onField(
                        'OEMManufacturerKey',
                        null
                      );
                      setShowManufacturerDropdown(true);
                    }}
                    onFocus={() => {
                      if (readOnly) return;
                      setShowManufacturerDropdown(true);
                    }}
                    onBlur={() =>
                      !readOnly &&
                      setTimeout(
                        () =>
                          setShowManufacturerDropdown(false),
                        200
                      )
                    }
                    placeholder="— Type to search manufacturer —"
                    readOnly={readOnly}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border:
                        '1px solid var(--modal-input-border-gray)',
                      borderRadius: '6px'
                    }}
                  />
                  {showManufacturerDropdown && !readOnly && (
                    <ul
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        background:
                          'var(--modal-bg-white-cell)',
                        border:
                          '1px solid var(--modal-input-border-gray)',
                        borderTop: 'none',
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        zIndex: 20,
                        boxShadow:
                          '0 2px 6px rgba(15,23,42,0.15)'
                      }}
                    >
                      {manufacturerMatches.length ? (
                        manufacturerMatches.map((m) => (
                          <li
                            key={m.OEMManufacturerKey}
                            onMouseDown={(e) =>
                              e.preventDefault()
                            }
                            onClick={() => {
                              setManufacturerSearch(
                                m.OEMManufacturer || ''
                              );
                              onField(
                                'OEMManufacturerKey',
                                m.OEMManufacturerKey
                              );
                              setShowManufacturerDropdown(
                                false
                              );
                            }}
                            style={{
                              padding: '8px 10px',
                              cursor: 'pointer',
                              borderBottom:
                                '1px solid var(--modal-border-gray)',
                              background:
                                form.OEMManufacturerKey ===
                                m.OEMManufacturerKey
                                  ? 'var(--modal-bg-gray)'
                                  : 'var(--modal-bg-white)',
                              color:
                                form.OEMManufacturerKey ===
                                m.OEMManufacturerKey
                                  ? 'var(--modal-text)'
                                  : 'inherit'
                            }}
                          >
                            {m.OEMManufacturer || '—'}
                          </li>
                        ))
                      ) : (
                        <li
                          style={{
                            padding: '8px 10px',
                            color: 'var(--modal-text-gray)'
                          }}
                        >
                          {oemManufacturers.length
                            ? 'No matches'
                            : 'No manufacturers available'}
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                <label
                  style={{
                    display: 'grid',
                    gap: '6px',
                    minWidth: 0
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    OEM Type
                  </span>
                  <input
                    value={form.OEMType || ''}
                    onChange={(e) =>
                      onField('OEMType', e.target.value)
                    }
                    readOnly={readOnly}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border:
                        '1px solid var(--modal-input-border-gray)',
                      borderRadius: '6px'
                    }}
                  />
                </label>

                <div
                  style={{
                    display: 'grid',
                    gap: '4px',
                    minWidth: 0
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    Last Updated
                  </span>
                  <span
                    style={{
                      fontSize: '13px',
                      color: 'var(--modal-text-dark)'
                    }}
                  >
                    {lastUpdatedDisplay}
                  </span>
                </div>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minHeight: '36px'
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    Needs Review
                  </span>
                  <input
                    type="checkbox"
                    checked={!!form.NeedsReview}
                    onChange={(e) =>
                      onField(
                        'NeedsReview',
                        e.target.checked
                      )
                    }
                    disabled={readOnly}
                  />
                </label>

                <label
                  style={{
                    display: 'grid',
                    gap: '6px',
                    minWidth: 0,
                    gridColumn: '1 / -1'
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'var(--modal-text-gray)'
                    }}
                  >
                    OEM Note
                  </span>
                  <textarea
                    value={form.OEMNote}
                    onChange={(e) =>
                      onField('OEMNote', e.target.value)
                    }
                    readOnly={readOnly}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border:
                        '1px solid var(--modal-input-border-gray)',
                      borderRadius: '6px',
                      minHeight: '96px',
                      resize: 'vertical'
                    }}
                  />
                </label>
              </div>

              {/* Image library */}
              <div
                className="pim-modal-card"
                style={{ display: 'grid', gap: '12px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    OEM Image Library
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleRefreshImages}
                      disabled={!form.OEMKey || imageBusy}
                      style={{
                        background:
                          'var(--modal-bg-light-blue)',
                        color: 'var(--modal-text-dark)',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '0px'
                      }}
                    >
                      Refresh
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleOpenImageModal()
                      }
                      disabled={!form.OEMKey}
                      style={{
                        background: 'var(--modal-bg-blue)',
                        color: 'var(--modal-text-white)',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '0px'
                      }}
                    >
                      Open Gallery
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                    alignItems: 'stretch'
                  }}
                >
                  <div
                    style={{
                      width: '188px',
                      minHeight: '150px',
                      border:
                        '1px dashed var(--modal-border-gray)',
                      borderRadius: '8px',
                      background: '#f7f9fc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {primaryImage ? (
                      <img
                        src={primaryImage.imageUrl}
                        alt="Primary OEM"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          color: 'var(--modal-text-gray)',
                          fontSize: '12px',
                          textAlign: 'center',
                          padding: '8px'
                        }}
                      >
                        No primary image
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      flex: '1 1 260px',
                      display: 'grid',
                      gap: '8px',
                      minWidth: 0
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--modal-text-gray)'
                      }}
                    >
                      Capture new image from URL
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap'
                      }}
                    >
                      <input
                        value={imageUrlInput}
                        onChange={(e) =>
                          setImageUrlInput(
                            e.target.value
                          )
                        }
                        placeholder="https://example.com/image"
                        disabled={
                          readOnly ||
                          !form.OEMKey ||
                          imageBusy
                        }
                        style={{
                          flex: '1 1 240px',
                          minWidth: '220px',
                          padding: '6px 8px',
                          border:
                            '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleCaptureImage}
                        disabled={
                          readOnly ||
                          !form.OEMKey ||
                          imageBusy ||
                          !imageUrlInput.trim() ||
                          !canCaptureMore
                        }
                        style={{
                          background:
                            'var(--modal-bg-blue)',
                          color:
                            'var(--modal-text-white)',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '0px'
                        }}
                      >
                        Capture
                      </button>
                    </div>

                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--modal-text-gray)'
                      }}
                    >
                      Slots used: {images.length} / 8
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: '6px',
                        flexWrap: 'wrap'
                      }}
                    >
                      {imageSlots.map((img, idx) => (
                        <button
                          key={`slot-${idx}`}
                          type="button"
                          onClick={() =>
                            img &&
                            handleOpenImageModal(
                              img.OEMImageKey
                            )
                          }
                          disabled={!img || !form.OEMKey}
                          title={
                            img
                              ? img.isPrimary
                                ? 'Primary image'
                                : 'View image details'
                              : 'Empty slot'
                          }
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: img
                              ? '1px solid var(--modal-border-gray)'
                              : '1px dashed var(--modal-border-gray)',
                            background: img
                              ? img.isPrimary
                                ? 'var(--modal-bg-blue)'
                                : 'var(--modal-bg-light-blue)'
                              : 'transparent',
                            color: img
                              ? img.isPrimary
                                ? 'var(--modal-text-white)'
                                : 'var(--modal-text-dark)'
                              : 'var(--modal-text-gray)',
                            cursor: img
                              ? 'pointer'
                              : 'default',
                            fontWeight: img?.isPrimary
                              ? 600
                              : 400
                          }}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>

                    {primaryImageAbsoluteUrl && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}
                      >
                        <button
                          type="button"
                          onClick={handleCopyPrimaryUrl}
                          style={{
                            background:
                              'var(--modal-bg-light-blue)',
                            color:
                              'var(--modal-text-dark)',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '0px'
                          }}
                        >
                          Copy URL
                        </button>
                        <button
                          type="button"
                          onClick={handleOpenPrimaryInNewTab}
                          style={{
                            background:
                              'var(--modal-bg-light-blue)',
                            color:
                              'var(--modal-text-dark)',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '0px'
                          }}
                        >
                          Open Full
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--modal-text-gray)'
                  }}
                >
                  Manage up to eight images per OEM. Use
                  the gallery to set defaults, reorder, or
                  delete.
                </div>
              </div>

              {/* Action buttons */}
              <div
                className="pim-modal-card"
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  marginTop: 'auto'
                }}
              >
                <button
                  style={{
                    background: 'var(--modal-bg-blue)',
                    color: 'var(--modal-text-white)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '0px'
                  }}
                  onClick={save}
                  disabled={readOnly || busy}
                >
                  {form.OEMKey ? 'Save' : 'Create'}
                </button>
                <button
                  style={{
                    background: 'var(--modal-bg-light-blue)',
                    color: 'var(--modal-text-dark)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '0px'
                  }}
                  onClick={duplicateCurrentOEM}
                  disabled={readOnly || busy || !form.OEMKey}
                  title={form.OEMKey ? 'Duplicate this OEM into a new OEM number' : 'Select or save an OEM first'}
                >
                  Duplicate
                </button>
                <button
                  style={{
                    background: 'var(--danger)',
                    color: 'var(--modal-text-white)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '0px'
                  }}
                  onClick={remove}
                  disabled={readOnly || !form.OEMKey || busy}
                >
                  Delete
                </button>
                {typeof onApply === 'function' && (
                  <button
                    style={{
                      background:
                        'var(--modal-bg-light-blue)',
                      color: 'var(--modal-text-dark)',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '0px'
                    }}
                    onClick={handleUseInKit}
                    disabled={readOnly || !form.OEMNumber}
                  >
                    Use in Kit
                  </button>
                )}
                <button
                  style={{
                    background:
                      'var(--modal-bg-light-blue)',
                    color: 'var(--modal-text-dark)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '0px'
                  }}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>

          {/* Usage table */}
          <div className="pim-modal-card">
            <div
              className="pim-modal-actions"
              style={{ justifyContent: 'space-between' }}
            >
              <div style={{ fontWeight: 600 }}>
                Usage
              </div>
              <div
                className="pim-modal-actions"
                style={{ marginLeft: 'auto' }}
              >
                <span
                  style={{
                    color: 'var(--modal-text-gray)',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  (
                  <strong>{filteredCount}</strong> /{' '}
                  {totalRows})
                </span>
                <button
                  style={{
                    background:
                      'var(--modal-bg-light-blue)',
                    color: 'var(--modal-text-dark)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '0px'
                  }}
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
                <button
                  style={{
                    background:
                      'var(--modal-bg-light-blue)',
                    color: 'var(--modal-text-dark)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '0px'
                  }}
                  onClick={copyUsage}
                  disabled={!filteredRows.length}
                >
                  Copy
                </button>
              </div>
            </div>

            <div
              className="pim-modal-scroll"
              style={{
                maxHeight: '300px',
                border: '1px solid var(--modal-border-gray)',
                borderRadius: '8px'
              }}
              onScroll={onGridScroll}
              ref={gridRef}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: '0px'
                }}
              >
                <thead>
                  <tr>
                    {['Make', 'Model', 'Years', 'Region', 'ItemType', 'FitmentLocation'].map(
                      (label) => (
                        <th
                          key={label}
                          style={{
                            background:
                              'var(--modal-bg-gray)',
                            textAlign: 'left',
                            fontWeight: 600,
                            fontSize: '13px',
                            padding: '8px 10px',
                            borderBottom:
                              '1px solid var(--modal-border-gray)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2
                          }}
                        >
                          {label}
                        </th>
                      )
                    )}
                  </tr>
                  <tr>
                    <th
                      style={{
                        background: 'var(--table-bg)',
                        padding: '6px 8px',
                        borderBottom:
                          '1px solid var(--modal-border-gray)',
                        position: 'sticky',
                        top: '34px',
                        zIndex: 1
                      }}
                    >
                      <input
                        placeholder="e.g. Hon*"
                        value={fMake}
                        onChange={(e) =>
                          setFMake(e.target.value)
                        }
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border:
                            '1px solid var(--modal-border-gray)',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                    </th>
                    <th
                      style={{
                        background: 'var(--table-bg)',
                        padding: '6px 8px',
                        borderBottom:
                          '1px solid var(--modal-border-gray)',
                        position: 'sticky',
                        top: '34px',
                        zIndex: 1
                      }}
                    >
                      <input
                        placeholder="Model…"
                        value={fModel}
                        onChange={(e) =>
                          setFModel(e.target.value)
                        }
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border:
                            '1px solid var(--modal-border-gray)',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                    </th>
                    <th
                      style={{
                        background: 'var(--table-bg)',
                        padding: '6px 8px',
                        borderBottom:
                          '1px solid var(--modal-border-gray)',
                        position: 'sticky',
                        top: '34px',
                        zIndex: 1
                      }}
                    >
                      <input
                        placeholder="1999 or 2000-2003"
                        value={fYears}
                        onChange={(e) =>
                          setFYears(e.target.value)
                        }
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border:
                            '1px solid var(--modal-border-gray)',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                    </th>
                    <th
                      style={{
                        background: 'var(--table-bg)',
                        padding: '6px 8px',
                        borderBottom:
                          '1px solid var(--modal-border-gray)',
                        position: 'sticky',
                        top: '34px',
                        zIndex: 1
                      }}
                    >
                      <input
                        placeholder="Region…"
                        value={fRegion}
                        onChange={(e) =>
                          setFRegion(e.target.value)
                        }
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border:
                            '1px solid var(--modal-border-gray)',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                    </th>
                    <th
                      style={{
                        background: 'var(--table-bg)',
                        padding: '6px 8px',
                        borderBottom:
                          '1px solid var(--modal-border-gray)',
                        position: 'sticky',
                        top: '34px',
                        zIndex: 1
                      }}
                    >
                      <input
                        placeholder="ItemType…"
                        value={fItemType}
                        onChange={(e) =>
                          setFItemType(e.target.value)
                        }
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border:
                            '1px solid var(--modal-border-gray)',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                    </th>
                    <th
                      style={{
                        background: 'var(--table-bg)',
                        padding: '6px 8px',
                        borderBottom:
                          '1px solid var(--modal-border-gray)',
                        position: 'sticky',
                        top: '34px',
                        zIndex: 1
                      }}
                    >
                      <input
                        placeholder="Location…"
                        value={fFitment}
                        onChange={(e) =>
                          setFFitment(e.target.value)
                        }
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border:
                            '1px solid var(--modal-border-gray)',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r, i) => (
                    <tr
                      key={`${r.Make}|${r.Model}|${r.Region}|${r.ItemType}|${r.FitmentLocation}|${i}`}
                    >
                      <td
                        style={{
                          padding: '8px 10px',
                          borderBottom:
                            '1px solid var(--modal-table-border-gray)',
                          fontSize: '13px',
                          background:
                            'var(--modal-bg-white-cell)'
                        }}
                      >
                        {r.Make}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          borderBottom:
                            '1px solid var(--modal-table-border-gray)',
                          fontSize: '13px',
                          background:
                            'var(--modal-bg-white-cell)'
                        }}
                      >
                        {r.Model}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          borderBottom:
                            '1px solid var(--modal-table-border-gray)',
                          fontSize: '13px',
                          background:
                            'var(--modal-bg-white-cell)'
                        }}
                      >
                        {r.Years}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          borderBottom:
                            '1px solid var(--modal-table-border-gray)',
                          fontSize: '13px',
                          background:
                            'var(--modal-bg-white-cell)'
                        }}
                      >
                        {r.Region}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          borderBottom:
                            '1px solid var(--modal-table-border-gray)',
                          fontSize: '13px',
                          background:
                            'var(--modal-bg-white-cell)'
                        }}
                      >
                        {r.ItemType}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          borderBottom:
                            '1px solid var(--modal-table-border-gray)',
                          fontSize: '13px',
                          background:
                            'var(--modal-bg-white-cell)'
                        }}
                      >
                        {r.FitmentLocation}
                      </td>
                    </tr>
                  ))}
                  {!visibleRows.length && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          padding: '8px 10px',
                          borderBottom:
                            '1px solid var(--modal-table-border-gray)',
                          fontSize: '13px',
                          background:
                            'var(--modal-bg-white-cell)',
                          color: 'var(--modal-text-gray)'
                        }}
                      >
                        No usage
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {imageModalOpen && (
        <ManageOEMImageGallery
          open={imageModalOpen}
          onClose={handleCloseImageModal}
          images={images}
          oemNumber={form.OEMNumber || ''}
          busy={imageBusy}
          onMakePrimary={handleMakePrimaryImage}
          onDelete={handleDeleteImage}
          onReorder={handleReorderImages}
          onRefresh={handleRefreshImages}
          initialImageKey={imageModalFocusKey}
          canEdit={canEdit}
        />
      )}
    </>
  );
}
